import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { ToolArgumentsProps } from './tool-arguments.interface';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatPrimitive(value: string | number | boolean | null) {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null) {
    return 'null';
  }

  return String(value);
}

function JsonNode({
  value,
  nested = false,
}: {
  value: unknown;
  nested?: boolean;
}) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Typography variant="body2">[]</Typography>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...(nested
            ? { pl: 1.5, borderLeft: 2, borderColor: 'divider' }
            : null),
        }}
      >
        {value.map((item, index) => (
          <JsonNode key={index} value={item} />
        ))}
      </Box>
    );
  }

  if (isRecord(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return <Typography variant="body2">{'{}'}</Typography>;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          ...(nested
            ? { pl: 1.5, borderLeft: 2, borderColor: 'divider' }
            : null),
        }}
      >
        {entries.map(([key, item]) => {
          const nestedValue = isRecord(item) || Array.isArray(item);

          return (
            <Box key={key}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: nestedValue ? 0.75 : 0.25 }}
              >
                {key}
              </Typography>
              <JsonNode value={item} nested={nestedValue} />
            </Box>
          );
        })}
      </Box>
    );
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return (
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {formatPrimitive(value)}
      </Typography>
    );
  }

  return null;
}

export function ToolArgumentsComponent({ value }: ToolArgumentsProps) {
  const parsed = useMemo(() => {
    const trimmed = value.trim();

    if (!trimmed) {
      return undefined;
    }

    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return value;
    }
  }, [value]);

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      {parsed === undefined ? null : <JsonNode value={parsed} />}
    </Box>
  );
}
